/**
 * What the Assets page reads and does, for every layout: the library as this person may see it, and a file's actions
 * (make a room, delete, move, restore). The computer's page and the phone's and iPad's both use it.
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import type { LibraryAsset, LibraryFolder } from "../../shared/types";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/ui/Toast";
import { useFlashkutAccount } from "./flashkut";
import { ALL, folderName, remapFolderId, restoreWords, visibleAssets, visibleFolders } from "./library";
import { deleteFile, libraryBlind, moveFile, restoreFiles, useFile, useLibrary } from "./store";

export function useAssetsData() {
  const lib = useLibrary();
  const flashkut = useFlashkutAccount();
  const { user } = useAuth();
  return {
    lib,
    flashkut,
    blind: libraryBlind(lib),
    // Why only a look for a viewer: the server refuses their changes; the page does not offer them.
    canChange: user?.role !== "viewer",
    folders: visibleFolders(lib.folders, flashkut),
    shown: visibleAssets(lib.assets, flashkut),
    trashed: visibleAssets(lib.deleted, flashkut).toSorted((x, y) => (y.deleted_at ?? 0) - (x.deleted_at ?? 0)),
  };
}

export function useFileActions(folders: LibraryFolder[]) {
  const toast = useToast();
  const navigate = useNavigate();
  const [making, setMaking] = useState<string | null>(null);

  // A talk is copied in the first time it is used; the room starts from that copy.
  const makeRoom = async (x: LibraryAsset) => {
    setMaking(x.id);
    try {
      const own = await useFile(x, "whole");
      navigate(`/rooms/new?asset=${encodeURIComponent(own.id)}`);
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : "That didn’t get ready. Try again.", error: true });
    } finally {
      setMaking(null);
    }
  };

  const remove = async (x: LibraryAsset) => {
    try {
      await deleteFile(x.id);
      toast({ text: `Moved ${x.name} to Trash.`, icon: "trash" });
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : "That file wasn’t deleted.", error: true });
    }
  };

  const move = (x: LibraryAsset, to: string | null) =>
    moveFile(x.id, to).then(
      () => toast(`Moved to ${folderName(folders, to)}.`),
      (e: unknown) => toast({ text: e instanceof Error ? e.message : "That file didn’t move.", error: true }),
    );

  const restore = async (ids: string[]) => {
    const res = await restoreFiles(ids);
    const said = restoreWords(res.restored, res.gone, res.folders);
    if (res.restored.length) toast(said);
    else toast({ text: said || res.failed[0] || "That didn’t come back.", error: true });
  };

  return { making, makeRoom, remove, move, restore };
}

/**
 * A folder renamed or moved has a new id, and so has everything inside it: the open folder follows it, or goes to `home`
 * once the folder is deleted.
 */
export function useFollowFolder(open: string, home: string) {
  const navigate = useNavigate();
  return (from: string, to: string | null) => {
    if (to === null) {
      if (open === from || open.startsWith(`${from}/`)) navigate(home, { replace: true });
      return;
    }
    if (open !== ALL) {
      const now = remapFolderId(open, from, to);
      if (now !== open) navigate(`/assets/${encodeURIComponent(now)}`, { replace: true });
    }
  };
}
